<template>
  <header>
    <div class="relative flex min-h-[65vh] flex-col">
      <!-- IMAGE OR VIDEO -->
      <div class="absolute inset-0 -z-10 overflow-hidden">
        <AppImage
          v-if="imageOrVideo.image"
          :src="imageOrVideo.image"
          :alt="
            $i18n.locale === 'fr'
              ? imageOrVideo.image.alt_fr
              : imageOrVideo.image.alt
          "
          class="h-full w-full object-cover"
          :style="`object-position: ${imageOrVideo.image.focus_css};`"
        />

        <AppImage
          v-else-if="
            imageOrVideo.video &&
            data.hero_only_image &&
            imageOrVideo.cover_image
          "
          :alt="
            $i18n.locale === 'fr'
              ? imageOrVideo.cover_image.alt_fr
              : imageOrVideo.cover_image.alt
          "
          :src="imageOrVideo.cover_image"
          class="h-full w-full object-cover"
          :style="`object-position: ${imageOrVideo.cover_image.focus_css};`"
        />

        <VideoPlayer
          v-else-if="imageOrVideo.video"
          :src="imageOrVideo.video"
          :autoplay="true"
          class="h-full w-full scale-[1.2] transition-transform"
        />
        <div
          v-else
          class="h-full w-full bg-gradient-to-tr from-blue-dark-800 to-blue-dark-600"
        />
      </div>

      <HeroImageGradient />

      <div
        class="z-10 flex w-full grow flex-col justify-end pb-12 pt-navigation-desktop text-white"
      >
        <Container indent="center">
          <div>
            <!-- BACK LINK -->
            <button
              class="group -m-1 my-5 flex items-center gap-3 p-1 text-base ring-white ring-offset-0 sm:my-7"
              @click="$router.back()"
            >
              <ArrowGrowIcon class="-ml-2 w-6 rotate-180" />
              <span class="first-letter:uppercase">
                {{ $t('back') }}
              </span>
            </button>

            <AnimatedContainer>
              <!-- TITLE -->
              <h1
                v-if="data.heading"
                class="fade-in mb-4 max-w-title hyphens-auto text-3xl font-medium leading-9 sm:mb-6 sm:text-6xl"
                v-html="$replaceHtmlTags(data.heading)"
              />

              <time
                :datetime="machineReadableDateTime"
                class="fade-in mb-7 inline-block text-white/60 sm:mb-12"
              >
                {{ dateTime }}
              </time>
            </AnimatedContainer>
            <NuxtLink
              :to="{
                path: `/${route.path.split('/')[1]}`,
                query: { newsType: data.news_type.slug },
              }"
            >
              <TypeTag layout="medium">
                {{ data.news_type.title }}
              </TypeTag>
            </NuxtLink>
          </div>
        </Container>
      </div>
    </div>

    <!-- LEAD -->
    <Container indent="center">
      <div
        v-if="data.lead"
        class="max-w-text pt-16 text-md font-medium"
        v-html="data.lead"
      />
    </Container>
  </header>
</template>

<script setup>
const { t } = useI18n()
const route = useRoute()

const props = defineProps({
  data: { type: Object, required: true },
})

const imageOrVideo = computed(() => {
  return props.data?.image_or_video?.[0] || false
})

const dateTime = computed(() => {
  if (!props.data?.date) {
    return null
  }

  let date = new Date(props.data.date).toLocaleDateString('de', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  if (props.data.time) {
    date += `, ${props.data.time} ${t('hrs')}`
  }

  return date
})

const machineReadableDateTime = computed(() => {
  if (!props.data?.date) {
    return null
  }

  if (!props.data.time) {
    return props.data.date
  }

  // Create a Date object using the date string
  const date = new Date(props.data.date)

  // Extract the hour and minute from the time string
  const [hour, minute] = props.data.time.split(':').map(Number)

  // Set the hour and minute to the Date object
  date.setHours(hour, minute)

  return date
})
</script>
